import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, Calendar, CheckCircle2, Clock, Trash2 } from 'lucide-react';
import { apiFetch } from '../../../utils/api';

interface ProjectMilestonesProps {
    projectId: string;
}

const ProjectMilestones: React.FC<ProjectMilestonesProps> = ({ projectId }) => {
    const [milestones, setMilestones] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newMilestone, setNewMilestone] = useState({
        name: '', description: '', start_date: '', end_date: ''
    });

    const fetchMilestones = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/project-delivery/milestones?project_id=${projectId}`);
            if (res.ok) setMilestones(await res.json());
        } catch (err) { console.error(err); } finally { setLoading(false); }
    }, [projectId]);

    useEffect(() => {
        fetchMilestones();
    }, [projectId, fetchMilestones]);

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/project-delivery/milestones', {
                method: 'POST',
                body: JSON.stringify({ ...newMilestone, project_id: projectId })
            });
            if (res.ok) {
                setShowAddModal(false);
                setNewMilestone({ name: '', description: '', start_date: '', end_date: '' });
                fetchMilestones();
            }
        } catch (err) { console.error(err); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this milestone?')) return;
        try {
            const res = await apiFetch(`/project-delivery/milestones/${id}`, { method: 'DELETE' });
            if (res.ok) fetchMilestones();
        } catch (err) { console.error(err); }
    };

    const handleUpdateStatus = async (id: string, status: string) => {
        try {
            const res = await apiFetch(`/project-delivery/milestones/${id}/status`, {
                method: 'PATCH',
                body: JSON.stringify({ status })
            });
            if (res.ok) fetchMilestones();
        } catch (err) { console.error(err); }
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-800">Project Milestones</h3>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-semibold"
                >
                    <Plus size={16} /> Add Milestone
                </button>
            </div>

            <div className="space-y-4">
                {milestones.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-gray-500">
                        No milestones defined yet.
                    </div>
                ) : (
                    milestones.map((m, i) => (
                        <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm flex items-start justify-between">
                            <div className="flex gap-4">
                                <div className={`p-3 rounded-full ${m.status === 'Completed' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>
                                    {m.status === 'Completed' ? <CheckCircle2 size={24} /> : <Clock size={24} />}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h4 className="font-bold text-gray-800">{m.name}</h4>
                                        <select
                                            className="text-[10px] bg-gray-50 border-none outline-none font-bold text-blue-600 rounded p-1"
                                            value={m.status}
                                            onChange={(e) => handleUpdateStatus(m.id, e.target.value)}
                                        >
                                            <option value="Pending">Pending</option>
                                            <option value="In Progress">In Progress</option>
                                            <option value="Completed">Completed</option>
                                        </select>
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">{m.description}</p>
                                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400 font-medium">
                                        <div className="flex items-center gap-1">
                                            <Calendar size={14} />
                                            <span>Starts: {new Date(m.start_date).toLocaleDateString()}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Calendar size={14} />
                                            <span>Ends: {new Date(m.end_date).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                                <span className={`text-xs font-bold uppercase px-2 py-1 rounded ${m.status === 'Completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {m.status}
                                </span>
                                <button
                                    onClick={() => handleDelete(m.id)}
                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {showAddModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full">
                        <h3 className="text-xl font-bold mb-4">Add Project Milestone</h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Milestone Name</label>
                                <input className="w-full border p-2 rounded-lg" value={newMilestone.name} onChange={e => setNewMilestone({ ...newMilestone, name: e.target.value })} required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                                <textarea className="w-full border p-2 rounded-lg" rows={3} value={newMilestone.description} onChange={e => setNewMilestone({ ...newMilestone, description: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Start Date</label>
                                    <input type="date" className="w-full border p-2 rounded-lg" value={newMilestone.start_date} onChange={e => setNewMilestone({ ...newMilestone, start_date: e.target.value })} required />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                                    <input type="date" className="w-full border p-2 rounded-lg" value={newMilestone.end_date} onChange={e => setNewMilestone({ ...newMilestone, end_date: e.target.value })} required />
                                </div>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 px-4 py-2 border rounded-lg font-bold">Cancel</button>
                                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectMilestones;
